import { useEffect, useState } from "react";
import { Download } from "lucide-react";

import { previewAsset, type AssetCard } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Props {
  asset: AssetCard | null;
  token: string | undefined;
  onClose: () => void;
  onDownload: (asset: AssetCard) => void;
}

/** Quick-View lightbox (D6): PDFs render inline in an <iframe>; any other
 * format falls back to a download-to-view prompt. Bytes are fetched with the
 * bearer token into an object URL (the preview endpoint isn't public). */
export function PreviewDialog({ asset, token, onClose, onDownload }: Props) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const isPdf = asset?.format === "PDF";

  useEffect(() => {
    if (!asset || !token || !isPdf) return;
    let revoked: string | null = null;
    let cancelled = false;
    setError(false);
    setObjectUrl(null);
    previewAsset(token, asset.id)
      .then(({ objectUrl: url }) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        revoked = url;
        setObjectUrl(url);
      })
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [asset, token, isPdf]);

  return (
    <Dialog open={asset !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{asset?.title}</DialogTitle>
          <DialogDescription>{asset?.description ?? "Preview"}</DialogDescription>
        </DialogHeader>
        {asset == null ? null : !isPdf ? (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <p className="text-sm text-muted-foreground">
              In-browser preview isn't available for {asset.format ?? "this format"}. Download to
              view.
            </p>
            <Button onClick={() => onDownload(asset)}>
              <Download className="h-4 w-4" aria-hidden /> Download to view
            </Button>
          </div>
        ) : error ? (
          <p role="alert" className="py-10 text-center text-sm text-muted-foreground">
            Couldn't load the preview.
          </p>
        ) : objectUrl ? (
          <iframe title={asset.title} src={objectUrl} className="h-[70vh] w-full rounded border" />
        ) : (
          <p role="status" className="py-10 text-center text-sm text-muted-foreground">
            Loading preview…
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
